import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout/Layout';
import { useRoadmapStore } from '../store/roadmapStore';
import { ROADMAP_WORKFLOW } from '../data/roadmapWorkflow';
import { RoadmapPhaseCard } from '../components/Roadmap/RoadmapPhaseCard';
import { RoadmapInteractionsPanel } from '../components/Roadmap/RoadmapInteractionsPanel';
import { RoadmapDocumentsPanel } from '../components/Roadmap/RoadmapDocumentsPanel';
import { exportRoadmapToPDF } from '../utils/roadmapPdfExport';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Building2,
  RefreshCw,
  Download,
  TrendingUp
} from 'lucide-react';

export const RoadmapDetailPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const {
    currentProject,
    isLoading,
    error,
    fetchProjectRoadmap,
    updateValidation,
    forceValidation,
    syncProjectAutoValidations
  } = useRoadmapStore();

  useEffect(() => {
    if (projectId) {
      fetchProjectRoadmap(projectId);
    }
  }, [projectId]);

  if (isLoading && !currentProject) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (error || !currentProject) {
    return (
      <Layout>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error || 'Impossible de charger la feuille de route'}
          </div>
        </div>
      </Layout>
    );
  }

  const { project, validations, progress, phaseProgresses } = currentProject;

  const handleExportPDF = () => {
    if (currentProject) {
      exportRoadmapToPDF(currentProject);
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        <button
          onClick={() => navigate('/roadmap')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à la liste
        </button>

        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <User className="w-5 h-5" />
                <span className="text-blue-100">
                  {project.owner?.full_name || 'Porteur inconnu'}
                </span>
              </div>

              <h1 className="text-3xl font-bold mb-2">{project.name}</h1>

              {project.description && (
                <p className="text-blue-100 mb-4">{project.description}</p>
              )}

              <div className="flex flex-wrap gap-4 text-sm">
                {project.owner?.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {project.owner.email}
                  </div>
                )}
                {project.owner?.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    {project.owner.phone}
                  </div>
                )}
                {project.owner?.organization && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    {project.owner.organization}
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col items-end gap-3">
              <span className="px-4 py-2 bg-white bg-opacity-20 backdrop-blur-sm rounded-lg text-sm font-medium">
                {project.status.replace(/_/g, ' ')}
              </span>

              <div className="text-right">
                <div className="flex items-center gap-2 text-sm mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span>Progression globale</span>
                </div>
                <div className="text-3xl font-bold">
                  {progress?.progress_percentage.toFixed(0)}%
                </div>
                <div className="text-sm text-blue-100 mt-1">
                  {progress?.completed_steps} / {progress?.total_steps} étapes
                </div>
              </div>
            </div>
          </div>

          <div className="w-full bg-white bg-opacity-20 backdrop-blur-sm rounded-full h-3 mt-6">
            <div
              className="bg-white h-3 rounded-full transition-all duration-300"
              style={{ width: `${progress?.progress_percentage || 0}%` }}
            />
          </div>
        </div>

        <div className="flex gap-6">
          <div className="flex-1 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                Parcours d'accompagnement
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => projectId && syncProjectAutoValidations(projectId)}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                  Synchroniser
                </button>
                <button
                  onClick={handleExportPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Export PDF
                </button>
              </div>
            </div>

            {ROADMAP_WORKFLOW.map((phase) => {
              const phaseProgress = phaseProgresses.find((p) => p.phase_key === phase.key);
              return (
                <RoadmapPhaseCard
                  key={phase.key}
                  phase={phase}
                  validations={validations}
                  phaseProgress={
                    phaseProgress || {
                      project_id: projectId || '',
                      phase_key: phase.key,
                      total_steps: 3,
                      completed_steps: 0,
                      progress_percentage: 0
                    }
                  }
                  onUpdateValidation={async (phaseKey, stepKey, data) => {
                    if (projectId) {
                      await updateValidation(projectId, phaseKey, stepKey, data);
                    }
                  }}
                  onForceValidation={async (phaseKey, stepKey, force) => {
                    if (projectId) {
                      await forceValidation(projectId, phaseKey, stepKey, force);
                    }
                  }}
                />
              );
            })}
          </div>

          <div className="w-80 space-y-6">
            {projectId && <RoadmapInteractionsPanel projectId={projectId} />}
            {projectId && <RoadmapDocumentsPanel projectId={projectId} />}
          </div>
        </div>
      </div>
    </Layout>
  );
};
